import { useMemo, memo } from "react"
import { User, MapPin, MapPinned, Landmark, TriangleAlert, ArrowLeftRight, Building2 } from "lucide-react"
import type { MapboxGeoJSONFeature } from "mapbox-gl"
import type { IParcel, POI } from "@/types/explore"
import type { PluInfoType } from "@/types/explore"
import type { IMutation } from "@/types/explore"
import type { POIMarker } from "@/types/explore"
import type { MutationMarker } from "@/types/explore"
import type { BuildingData } from "@/types/explore"
import { DetailAccordion } from "./detail-accordion"
import { BuildingDetails } from "../details/building-details"
import { RisquesDetails } from "../details/risques-details"
import { UrbanismeDetails } from "../details/urbanisme-details"
import { PoiList } from "../details/poi-list"
import { OwnerCard } from "../details/owner-card"
import { PLUDetails } from "../details/plu-details"

interface ParcelCardDetailsProps {
  selectedParcel: IParcel
  selectedPLU: MapboxGeoJSONFeature | null
  setParcelPOI: (poi: { lon: number | null; lat: number | null }) => void
  onPLULoaded?: (plu: PluInfoType) => void
  onFlyTo?: (center: [number, number]) => void
  onActivePOIsChange?: (pois: POIMarker[]) => void
  onHoverPOI?: (poi: POIMarker | null) => void
  onActiveMutationsChange?: (markers: MutationMarker[]) => void
  // Data props (fetched by parent or passed down)
  parcel?: Record<string, unknown> | null
  mutations?: IMutation[]
  mutationsLoading?: boolean
  pois?: POI[]
  poisLoading?: boolean
  poisError?: boolean
  effectivePLU?: PluInfoType | null
  pluLoading?: boolean
  pluError?: boolean
  pluFiles?: { name: string; path: string; title: string }[]
  pluFilesLoading?: boolean
  urbanismeData?: Record<string, unknown> | null
  urbanismeLoading?: boolean
  urbanismeError?: string | null
  risquesData?: Record<string, unknown> | null
  risquesLoading?: boolean
  risquesError?: string | null
  buildingData?: BuildingData[] | null
  buildingLoading?: boolean
  buildingError?: boolean
  assocData?: Record<string, unknown>[] | null
  assocLoading?: boolean
}

export const ParcelCardDetails = memo(
  ({
    selectedParcel,
    setParcelPOI,
    onFlyTo,
    onActivePOIsChange,
    onHoverPOI,
    mutations = [],
    mutationsLoading = false,
    pois = [],
    poisLoading = false,
    poisError = false,
    effectivePLU = null,
    pluLoading = false,
    pluError = false,
    urbanismeData = null,
    urbanismeLoading = false,
    urbanismeError = null,
    risquesData = null,
    risquesLoading = false,
    risquesError = null,
    buildingData = null,
    buildingLoading = false,
    buildingError = false,
  }: ParcelCardDetailsProps) => {
    const { idu } = selectedParcel?.properties || {}
    const filteredMutations = useMemo(
      () => (mutations && idu ? mutations.filter((m: IMutation) => m.id_parcelle === idu) : []),
      [mutations, idu],
    )

    const showPluRow = pluLoading || !!effectivePLU

    // Summary computations
    const pluSummary = effectivePLU?.libelle || effectivePLU?.typezone || null

    const ownerSummary = useMemo(() => {
      const owners = selectedParcel?.corporationOwners
      if (!owners || owners.length === 0) return "Non renseigne"
      if (owners.length === 1) return owners[0].denomination || owners[0].siren || "Non renseigne"
      return `${owners.length} proprietaires`
    }, [selectedParcel?.corporationOwners])

    const risquesSummary = useMemo(() => {
      const risquesNaturels = (risquesData as Record<string, unknown>)?.risquesNaturels as
        | Record<string, Record<string, unknown>>
        | undefined
      const risquesTechno = (risquesData as Record<string, unknown>)?.risquesTechnologiques as
        | Record<string, Record<string, unknown>>
        | undefined
      const natCount = risquesNaturels ? Object.values(risquesNaturels).filter((r) => r?.present).length : 0
      const techCount = risquesTechno ? Object.values(risquesTechno).filter((r) => r?.present).length : 0
      const totalRisks = natCount + techCount
      return risquesData ? (totalRisks > 0 ? `${totalRisks} risque${totalRisks > 1 ? "s" : ""}` : "Aucun") : null
    }, [risquesData])

    const mutationsSummary = useMemo(
      () =>
        filteredMutations.length > 0
          ? `${filteredMutations.length} vente${filteredMutations.length > 1 ? "s" : ""}`
          : "Aucune",
      [filteredMutations.length],
    )

    const buildingSummary = useMemo(() => {
      const b0 = buildingData && buildingData.length > 0 ? buildingData[0] : null
      return b0
        ? [b0.hauteur_mean ? `${Math.round(b0.hauteur_mean)}m` : null, b0.nb_niveau ? `Etage ${b0.nb_niveau}` : null]
            .filter(Boolean)
            .join(" \u00B7 ") || null
        : null
    }, [buildingData])

    const poisSummary = useMemo(
      () => (!poisLoading ? (pois.length > 0 ? `${pois.length} proche${pois.length > 1 ? "s" : ""}` : "Aucun") : null),
      [poisLoading, pois.length],
    )

    const urbanismeSummary = useMemo(() => {
      if (!urbanismeData) return null
      const urbanismeFeatCount = Object.values(urbanismeData).filter(
        (v) => v && (Array.isArray(v) ? v.length > 0 : true),
      ).length
      return urbanismeFeatCount > 0 ? `${urbanismeFeatCount} couche${urbanismeFeatCount > 1 ? "s" : ""}` : "\u2014"
    }, [urbanismeData])

    return (
      <div data-tour-step="parcel-details" className="pb-2">
        <DetailAccordion
          title="Proprietaire personne morale"
          icon={<User className="w-5 h-5" />}
          loading={false}
          error={false}
          summary={ownerSummary}
          defaultOpen
        >
          {selectedParcel?.corporationOwners && selectedParcel.corporationOwners.length > 0 ? (
            <OwnerCard parcel={selectedParcel} />
          ) : (
            <p className="text-sm text-gray-400 py-2">Aucune information proprietaire disponible</p>
          )}
        </DetailAccordion>

        <DetailAccordion
          title="Points d'interet"
          icon={<MapPin className="w-5 h-5" />}
          loading={poisLoading}
          error={poisError}
          summary={poisSummary}
        >
          <PoiList
            pois={pois}
            setParcelPOI={setParcelPOI}
            onFlyTo={onFlyTo}
            onActivePOIsChange={onActivePOIsChange}
            onHoverPOI={onHoverPOI}
          />
        </DetailAccordion>

        {showPluRow && (
          <DetailAccordion
            title="PLU"
            icon={<MapPinned className="w-5 h-5" />}
            loading={pluLoading}
            error={!!pluError}
            summary={pluSummary}
          >
            {effectivePLU && <PLUDetails selectedPLU={effectivePLU} />}
          </DetailAccordion>
        )}

        <DetailAccordion
          title="Urbanisme"
          icon={<Landmark className="w-5 h-5" />}
          loading={urbanismeLoading}
          error={!!urbanismeError}
          summary={urbanismeSummary}
        >
          <UrbanismeDetails data={urbanismeData as Record<string, unknown>} error={urbanismeError} />
        </DetailAccordion>

        <DetailAccordion
          title="Risques"
          icon={<TriangleAlert className="w-5 h-5" />}
          loading={risquesLoading}
          error={!!risquesError}
          summary={risquesSummary}
        >
          <RisquesDetails data={risquesData as Record<string, unknown>} error={risquesError} />
        </DetailAccordion>

        <DetailAccordion
          title="Mutations"
          icon={<ArrowLeftRight className="w-5 h-5" />}
          loading={mutationsLoading}
          error={false}
          summary={mutationsSummary}
        >
          <div className="text-sm text-gray-600">
            {filteredMutations.length > 0 ? `${filteredMutations.length} mutation(s) trouvee(s)` : "Aucune mutation"}
          </div>
        </DetailAccordion>

        <DetailAccordion
          title="Batiment"
          icon={<Building2 className="w-5 h-5" />}
          loading={buildingLoading}
          error={buildingError}
          summary={buildingSummary}
        >
          <BuildingDetails buildingData={buildingData} />
        </DetailAccordion>
      </div>
    )
  },
)
